// ── VoiceConversationOverlay (Evo Brain — modo de conversación por voz) ──────
// HUD flotante, montado una vez en AppShell (junto a AssistantDock — mismo
// criterio: vive arriba de las páginas, sobrevive a la navegación). Botón
// visible + atajo (useVoiceConversationMode) para abrir; cerrado, es un
// botón chico flotante; abierto, es una tarjeta compacta con el estado
// (escuchando/procesando/hablando) y, si hay una propuesta pendiente, la
// ApprovalCard de siempre — misma UI que ya usa el dock, sin duplicar nada.
//
// Posición: bottom-center, para no pisar el dock (borde derecho) ni el
// sidebar (borde izquierdo). position:fixed justificado — como Modal.tsx y
// SearchOverlay.tsx, tiene que flotar sobre cualquier página sin importar
// scroll o ruta.

import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { IconButton } from '../ui/IconButton.js';
import { ApprovalCard, type ApprovalProposalItem } from '../ui/ApprovalCard.js';
import { useActiveAssistant } from '../../contexts/AssistantContext.js';
import { useVoiceConversationMode, type VoiceModePhase } from '../../hooks/useVoiceConversationMode.js';

// `twinkle` en vez de girar para el sparkle: son dos estrellas asimétricas, y
// rotarlas las hacía cabecear alrededor de un centro que no es el suyo. Cada
// una destella en su lugar, alternadas (ver .evo-sparkle-loading en index.css).
const PHASE_ICON: Record<VoiceModePhase, { name: 'mic' | 'volume' | 'sparkle' | 'alert'; twinkle?: boolean; pulse?: boolean }> = {
  closed:             { name: 'mic' },
  initializing:       { name: 'sparkle', twinkle: true },
  listening:          { name: 'mic', pulse: true },
  processing:         { name: 'sparkle', twinkle: true },
  speaking:           { name: 'volume', pulse: true },
  awaiting_approval:  { name: 'alert' },
  mic_error:          { name: 'alert' },
};

function StateIcon({ phase }: { phase: VoiceModePhase }) {
  const def = PHASE_ICON[phase];
  const color = phase === 'mic_error' ? 'var(--evo-danger)'
    : phase === 'awaiting_approval' ? 'var(--evo-warning)'
    : 'var(--evo-cyan)';
  return (
    <span
      className={def.twinkle ? 'evo-sparkle-loading' : undefined}
      style={{
        display: 'inline-flex',
        color,
        // El destello del sparkle se anima path por path desde CSS (no acá):
        // el contenedor no se toca, porque escalar el conjunto reintroduce
        // justamente el desplazamiento que se quiso sacar.
        animation: def.pulse ? 'evo-mic-pulse 1s ease-in-out infinite' : 'none',
      }}
    >
      <Icon name={def.name} size={16} />
    </span>
  );
}

export function VoiceConversationOverlay() {
  const { t } = useTranslation();
  const voice = useVoiceConversationMode();
  // Segunda suscripción a "quién está activo" — useSyncExternalStore soporta
  // múltiples lectores sin problema (el loop que este store evitó era sobre
  // ESCRITURA, no lectura — ver el comentario grande en AssistantContext.tsx).
  const active = useActiveAssistant();

  if (voice.phase === 'closed') {
    return (
      <div style={{ position: 'fixed', bottom: 'var(--evo-space-5)', left: '50%', transform: 'translateX(-50%)', zIndex: 150 }}>
        <IconButton
          icon={<Icon name="mic" size={18} />}
          ariaLabel={t('assistant.voiceMode.openLabel', { defaultValue: 'Modo de voz' })}
          tone="muted"
          variant="outline"
          shape="circle"
          size="lg"
          onClick={voice.open}
        />
      </div>
    );
  }

  const label = voice.phase === 'mic_error'
    ? (voice.micError ?? '')
    : t(`assistant.voiceMode.${voice.phase === 'awaiting_approval' ? 'awaitingApproval' : voice.phase}`, {
        defaultValue: voice.phase,
      });

  const pendingProposals: ApprovalProposalItem[] = (active?.api.messages ?? [])
    .filter(m => m.role === 'tool_proposal' && m.proposal?.status === 'pending')
    .map(m => ({
      id: m.id,
      taskId: m.proposal!.taskId,
      turnGroupId: m.proposal!.turnGroupId,
      toolName: m.toolName ?? '',
      verb: m.proposal!.verb,
      sentence: m.proposal!.summary.sentence,
      changes: m.proposal!.summary.changes,
      irreversible: m.proposal!.summary.irreversible,
      warning: m.proposal!.summary.warning,
      createdAt: m.createdAt,
      status: m.proposal!.status,
    }));

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'var(--evo-space-5)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 150,
        width: 'min(420px, calc(100vw - 32px))',
        background: 'var(--evo-bg-2)',
        border: `1px solid ${voice.phase === 'mic_error' ? 'var(--evo-danger)' : 'var(--evo-stroke)'}`,
        borderRadius: 'var(--evo-r-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--evo-space-2)',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <StateIcon phase={voice.phase} />
        <span style={{
          flex: 1,
          fontSize: 'var(--evo-fs-label)',
          fontWeight: 'var(--evo-fw-medium)',
          fontFamily: 'var(--evo-font)',
          color: 'var(--evo-fg-1)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
        <IconButton
          icon={<Icon name="close" size={14} />}
          ariaLabel={t('assistant.voiceMode.closeLabel', { defaultValue: 'Cerrar modo de voz' })}
          tone="muted"
          variant="ghost"
          size="sm"
          onClick={voice.close}
        />
      </div>

      {voice.phase === 'awaiting_approval' && (
        <>
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
            {t('assistant.voiceMode.awaitingApprovalHint', {
              defaultValue: 'Aprobá o rechazá con el mouse — no se escucha hasta que resuelvas esto.',
            })}
          </p>
          {pendingProposals.length > 0 && (
            <ApprovalCard
              proposals={pendingProposals}
              entity={active?.config.entityLink}
              onApprove={(taskId, remember) => void active?.api.approveProposal(taskId, remember)}
              onReject={taskId => void active?.api.rejectProposal(taskId)}
              approvingId={active?.api.approvingTaskId ?? null}
              rejectingId={active?.api.rejectingTaskId ?? null}
            />
          )}
        </>
      )}

      {voice.phase !== 'mic_error' && voice.phase !== 'awaiting_approval' && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>
          {t('assistant.voiceMode.shortcutHint', { defaultValue: 'Ctrl+Shift+Espacio · Esc para cerrar' })}
        </span>
      )}
    </div>
  );
}
